// Fetches a user's avatar bytes and turns them into a data: URL — deliberately
// NOT an object URL. Object URLs need a revoke lifecycle (see download.ts); a
// data URL has none, and react-query already caches the string forever
// (staleTime Infinity in useAvatar.ts), so there's no leak to manage.

import { apiClient } from '../api/client.js'

const CHUNK_SIZE = 8 * 1024

/** ArrayBuffer -> base64 without spreading the whole byte array onto the call
 *  stack (`String.fromCharCode(...bytes)` blows it past a few hundred KB). */
function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let binary = ''
  for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
    const chunk = bytes.subarray(i, i + CHUNK_SIZE)
    binary += String.fromCharCode(...chunk)
  }
  return btoa(binary)
}

/** GET /users/:id/avatar as a data: URL, or null if the user has no photo
 *  (404). Any other failure rethrows so the caller can surface a real error
 *  instead of silently drawing initials for e.g. a network outage. */
export async function fetchAvatarDataUrl(userId: string): Promise<string | null> {
  try {
    const res = await apiClient.get<Blob>(`/users/${encodeURIComponent(userId)}/avatar`, {
      responseType: 'blob',
    })
    const buf = await res.data.arrayBuffer()
    const b64 = arrayBufferToBase64(buf)
    return `data:${res.data.type};base64,${b64}`
  } catch (err) {
    const status = (err as { response?: { status?: number } }).response?.status
    if (status === 404) return null
    throw err
  }
}
